// Global command palette (⌘K / Ctrl+K). Searches every navigable destination,
// switches products, runs quick actions, and jumps to a fleet asset. Built on
// cmdk and styled to the console design system (overlay surface, hairlines).

import { useEffect, useRef } from "react";
import { Command } from "cmdk";
import { useNavigate } from "react-router-dom";
import { useQuery } from "urql";
import {
  ArrowRight,
  Cpu,
  LogOut,
  Plus,
  Search,
  Settings as SettingsIcon,
} from "lucide-react";

import { TenantAssetsQuery } from "@/app/queries";
import { useAuth } from "@/lib/auth/AuthProvider";
import { allNavEntries, visibleApps } from "@/app/products";
import { Kbd, ProductIcon } from "@/app/console/ui";

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { roles, logout } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);

  // Only fetch assets while the palette is open (pause otherwise).
  const [assetsResult] = useQuery({ query: TenantAssetsQuery, pause: !open });
  const assets = assetsResult.data?.tenantAssets ?? [];

  const navEntries = allNavEntries(roles);
  const apps = visibleApps(roles);

  // Focus the input when opened.
  useEffect(() => {
    if (open) {
      const id = window.setTimeout(() => inputRef.current?.focus(), 10);
      return () => window.clearTimeout(id);
    }
  }, [open]);

  // Lock background scroll while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  function go(to: string) {
    onClose();
    navigate(to);
  }

  async function signOut() {
    onClose();
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <div
      className="fixed inset-0 z-palette flex items-start justify-center bg-black/60 px-4 pt-[12vh] backdrop-blur-[2px]"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <Command
        label="Console command menu"
        className="w-full max-w-xl overflow-hidden rounded-card border border-line-strong bg-surface-3 shadow-[0_24px_64px_rgba(0,0,0,0.55)]"
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            onClose();
          }
        }}
      >
        <div className="flex items-center gap-2 border-b border-line px-3">
          <Search className="h-4 w-4 shrink-0 text-text-3" aria-hidden="true" />
          <Command.Input
            ref={inputRef}
            placeholder="Search pages, products, assets, actions…"
            className="h-12 w-full bg-transparent text-sm text-text-1 placeholder-text-4 outline-none coarse:text-base"
          />
          <Kbd className="hidden sm:inline-flex">esc</Kbd>
        </div>

        <Command.List className="max-h-[60vh] overflow-y-auto p-2">
          <Command.Empty className="py-8 text-center text-sm text-text-3">
            No results found.
          </Command.Empty>

          <Command.Group heading="Quick actions" className="palette-group">
            <PaletteRow
              value="forecast price prediction rtm dam run"
              icon={<Plus className="h-4 w-4" />}
              label="Open price forecasts"
              onSelect={() => go("/trade/forecasts")}
            />
            <PaletteRow
              value="launch backtest new run"
              icon={<Plus className="h-4 w-4" />}
              label="Launch a backtest"
              onSelect={() => go("/trade/backtests")}
            />
            <PaletteRow
              value="account settings profile mfa preferences"
              icon={<SettingsIcon className="h-4 w-4" />}
              label="Account settings"
              onSelect={() => go("/settings")}
            />
            <PaletteRow
              value="sign out logout"
              icon={<LogOut className="h-4 w-4" />}
              label="Sign out"
              onSelect={signOut}
            />
          </Command.Group>

          <Command.Group heading="Products" className="palette-group">
            {apps.map((app) => {
              const Icon = app.icon;
              return (
                <PaletteRow
                  key={app.id}
                  value={`product ${app.name} ${app.tagline}`}
                  icon={<ProductIcon icon={Icon} accent={app.accent} size="xs" style={{ width: 24, height: 24, borderRadius: 6 }} />}
                  label={app.name}
                  hint={app.tagline}
                  onSelect={() => go(app.home)}
                />
              );
            })}
          </Command.Group>

          <Command.Group heading="Go to" className="palette-group">
            {navEntries
              .filter((e) => !e.soon)
              .map((entry) => {
                const Icon = entry.icon;
                return (
                  <PaletteRow
                    key={entry.to}
                    value={`${entry.label} ${entry.appName} ${entry.hint ?? ""}`}
                    icon={<Icon className="h-4 w-4" />}
                    label={entry.label}
                    hint={`${entry.appName}${entry.hint ? ` · ${entry.hint}` : ""}`}
                    onSelect={() => go(entry.to)}
                  />
                );
              })}
          </Command.Group>

          {assets.length > 0 && (
            <Command.Group heading="Assets" className="palette-group">
              {assets.map((a) => (
                <PaletteRow
                  key={a.id}
                  value={`asset ${a.name} ${a.locationState}`}
                  icon={<Cpu className="h-4 w-4" />}
                  label={a.name}
                  hint={`${a.locationState} · ${a.capacityMwh.toFixed(0)} MWh`}
                  onSelect={() => go(`/asset/sites/${a.id}`)}
                />
              ))}
            </Command.Group>
          )}
        </Command.List>
      </Command>
    </div>
  );
}

function PaletteRow({
  value,
  icon,
  label,
  hint,
  onSelect,
}: {
  value: string;
  icon: React.ReactNode;
  label: string;
  hint?: string;
  onSelect: () => void;
}) {
  return (
    <Command.Item
      value={value}
      onSelect={onSelect}
      className="group flex h-10 cursor-pointer items-center gap-3 rounded-control px-2 text-sm text-text-2 outline-none data-[selected=true]:bg-white/[0.06] data-[selected=true]:text-text-1 coarse:h-11"
    >
      <span className="flex w-6 shrink-0 items-center justify-center text-text-3 group-data-[selected=true]:text-text-1">{icon}</span>
      <span className="flex-1 truncate">{label}</span>
      {hint && <span className="truncate text-xs text-text-3">{hint}</span>}
      <ArrowRight
        className="h-3.5 w-3.5 shrink-0 text-text-3 opacity-0 transition-opacity group-data-[selected=true]:opacity-100"
        aria-hidden="true"
      />
    </Command.Item>
  );
}
